/**
 * The address in four parts — D-039 (round 8, rulings 11–12).
 *
 * Stephanie asked for city, state and ZIP as their own fields, in the
 * database as their own columns. So B2 (Street address) is joined by B2a
 * City, B2b State and B2c ZIP, and `surveys.address` by `city`/`state`/`zip`.
 * But every place that PRINTS an address prints one line — the Telegram
 * notice, the cut sheet's job block, the office row's subtitle, Home's title
 * when there is no building name — and this module is the one function that
 * composes it, so the four never drift into four different spellings.
 *
 * The composition rule is "street, city, state zip" with a missing part
 * skipped and no comma left behind for it. That rule is what lets an OLD
 * record — everything typed into B2, no parts at all — print exactly the
 * string it always did: the street is the whole line, and there is nothing
 * to append. Nothing here parses a one-line address into parts; a record is
 * split only when a person split it.
 *
 * Pure: no clock, no store, no DOM. Runs on the phone and on the Worker.
 */

import { answerKey, type SurveyDocument } from './types';

export interface AddressParts {
  street?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
}

/** A part is present when it has any non-whitespace text; it travels trimmed. */
function present(value: string | null | undefined): string {
  return value?.trim() ?? '';
}

/**
 * "street, city, state zip". Every part optional; a missing one is skipped
 * without a dangling comma. State and ZIP share a segment because that is how
 * an address is written, so `{ state: 'TX', zip: '77030' }` is "TX 77030" and
 * either alone is itself.
 */
export function formatAddress(parts: AddressParts): string {
  const street = present(parts.street);
  const city = present(parts.city);
  const region = [present(parts.state), present(parts.zip)].filter((s) => s !== '').join(' ');
  return [street, city, region].filter((s) => s !== '').join(', ');
}

/**
 * B2 / B2a / B2b / B2c off a document. Building-level questions, so their
 * canonical keys are the bare ids — the same read `notify.ts` and
 * `queries.ts` make for B1/B2. A part that was never answered, or answered
 * with only whitespace, comes back as '' so `formatAddress` skips it.
 */
export function addressPartsOf(document: SurveyDocument): Required<AddressParts> {
  const text = (questionId: string): string => {
    const value = document.answers[answerKey(questionId)];
    return value?.kind === 'text' ? value.value.trim() : '';
  };
  return { street: text('B2'), city: text('B2a'), state: text('B2b'), zip: text('B2c') };
}
